import { useState, useRef } from "react";
import { createPortal } from "react-dom";
import { X, Play, Pause, Download } from "lucide-react";

export default function AnnouncementModal({ isOpen, onClose }) {
  const [voice, setVoice] = useState("male");
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  const audioRef = useRef(null);

  // Audio sources
  const sources = {
    male:
      localStorage.getItem("male_announcement") ||
      "https://music-cdn.tringbox.com/Tb_ann_male.mp3",
    female:
      localStorage.getItem("female_announcement") ||
      "https://music-cdn.tringbox.com/Tb_ann_female.mp3",
  };
  const handleDownload = () => {
    const link = document.createElement("a");
    link.href = sources[voice] + "?download=1"; // query param hack for some CDNs
    link.setAttribute("download", `announcement-${voice}.mp3`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play();
      setIsPlaying(true);
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const handleSeek = (e) => {
    const newTime = e.target.value;
    if (audioRef.current) {
      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  const formatTime = (time) => {
    if (!time || isNaN(time)) return "0:00";
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60)
      .toString()
      .padStart(2, "0");
    return `${minutes}:${seconds}`;
  };

  if (!isOpen || typeof document === "undefined") return null;

  return createPortal(
    <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-[100]">
      <div
        className="w-full h-full absolute top-0 left-0 z-0 "
        onClick={onClose}
      />
      <div className="bg-white w-[90%] max-w-md rounded-2xl shadow-xl p-6 relative">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-500 hover:text-gray-700"
        >
          <X size={20} />
        </button>

        {/* Title */}
        <div className="flex items-center justify-center gap-2 mb-4">
          <span className="text-2xl">📢</span>
          <h2 className="text-lg font-semibold text-purple-700">Announce</h2>
        </div>

        {/* Voice Selection */}
        <p className="text-gray-700 text-sm mb-2">Select Voice</p>
        <div className="flex gap-6 mb-6">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="voice"
              value="male"
              checked={voice === "male"}
              onChange={() => setVoice("male")}
              className="text-purple-600 focus:ring-purple-500"
            />
            <span className="text-gray-800">Male Voice</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="radio"
              name="voice"
              value="female"
              checked={voice === "female"}
              onChange={() => setVoice("female")}
              className="text-purple-600 focus:ring-purple-500"
            />
            <span className="text-gray-800">Female Voice</span>
          </label>
        </div>

        {/* Audio Player */}
        <audio
          ref={audioRef}
          src={sources[voice]}
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleLoadedMetadata}
          onEnded={() => setIsPlaying(false)}
        />

        <div className="flex flex-col items-center gap-3">
          <button
            onClick={togglePlay}
            className="bg-purple-600 text-white rounded-full p-4 hover:bg-purple-700"
          >
            {isPlaying ? <Pause size={28} /> : <Play size={28} />}
          </button>
          <div className="flex items-center w-full gap-2 text-gray-700 text-sm">
            <span>{formatTime(currentTime)}</span>
            <input
              type="range"
              min="0"
              max={duration}
              value={currentTime}
              onChange={handleSeek}
              className="w-full accent-purple-600"
            />
            <span>{formatTime(duration)}</span>
          </div>
          <p className="text-xs text-gray-500">
            {isPlaying ? "Playing..." : "Ready to play"}
          </p>
        </div>

        {/* Download Button */}
        <button
          onClick={handleDownload}
          className="w-full mt-6 flex items-center justify-center gap-2 border border-purple-600 text-purple-600 font-medium rounded-lg py-2 hover:bg-purple-50"
        >
          <Download size={18} />
          Download MP3
        </button>
      </div>
    </div>,
    document.body
  );
}
